import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faDatabase, faUndo, faWrench } from '@fortawesome/free-solid-svg-icons';

import { useToggle } from '@hooks/useToggle';

import { localStorageHyperDashboardTools } from '@utils/tools/localStorageHyperDashboardTools';
import { preventerTools } from '@utils/tools/preventerTools';

import { changeData } from '@actions/ssap/ssapActionCreators';

import CheckboxForSettings from '../inputs/CheckboxForSettings';
import RegularInputForSettings from '../inputs/RegularInputForSettings';
import DropdownForSettings from '../inputs/DropdownsForSettings';
import ReduxStoreInfo from './ReduxStoreInfo';

import { darculaIconButtonBase, darculaSettingsStyles } from '../darculaTheme';

const {
    settingsShellBase,
    settingsShellOpen,
    settingsShellCollapsed,
    settingsToolbarRowStyle,
    settingsAdvancedToolsGroupStyle,
    settingsAdvancedToolsTogglesRowStyle,
    settingRowWrapper,
    settingRowWrapperInlineClusterStyle,
    settingsTogglePressed,
    settingsToggleReleased,
    settingsResetButtonStyle,
} = darculaSettingsStyles;

/**
 * Dev / console toggles shown at the end of settings, behind the wrench control.
 * Order matches product copy (labels come from localStorageConfigs).
 */
const CONSOLE_TOOL_KEYS = [
    'isReactIntlWarningsShown',
    'isValidatorsGeneratorResultsShown',
    'isInvalidInputsShown',
    'isFormValidationShown',
    'isPreventerConsoleShown',
    'areNonStandaloneConsolesShown',
    'areSentToBackendConsolesShown',
    'isCaseNumberConsoleShown',
    'isDataSyncConsolesShown',
];

const consoleToolKeySet = new Set(CONSOLE_TOOL_KEYS);

/** Rendered on the HyperDashboard shell next to Route; omit from the settings panel. */
const TOP_LEVEL_SETTING_KEYS = new Set([ 'stateCode' ]);

const isSettingEntryVisible = (entry, currentLocation) => {

    const [
        ,
        {
            shouldBeShown = undefined,
            shouldNotBeShown = undefined,
        }
    ] = entry;

    if (![ shouldBeShown, shouldNotBeShown ].every(v => v === undefined)) {
        if (preventerTools.provideArray(shouldNotBeShown).some(path => path === currentLocation)) {
            return false;
        }

        if (!preventerTools.provideArray(shouldBeShown).some(path => path === currentLocation)) {
            return false;
        }
    }

    return true;
};

const Settings = ({ currentLocation }) => {

    const data = useSelector(state => state.ssap.data);
    const dispatch = useDispatch();
    const store = useStore();

    const [ areSettingsShown, toggleVisibility ] = useToggle(false);
    const [ consoleToolsOpen, setConsoleToolsOpen ] = useState(false);
    const [ isReduxStoreOpen, setIsReduxStoreOpen ] = useState(false);

    const settings = localStorageHyperDashboardTools.settings;

    const hasConsoleTools = useMemo(() => {

        if (!settings) {
            return false;
        }

        const entries = Object.entries(settings);

        return CONSOLE_TOOL_KEYS.some(key => {

            const entry = entries.find(([ k ]) => k === key);

            return Boolean(entry && isSettingEntryVisible(entry, currentLocation));
        });
    }, [ settings, currentLocation ]);

    useEffect(() => {

        if (!areSettingsShown) {
            setConsoleToolsOpen(false);
        }
    }, [ areSettingsShown ]);

    useEffect(() => {
        const closeReduxPanelOnBlockCloseClick = e => {
            if (e.target.id === 'ReduxStore') {
                setIsReduxStoreOpen(false);
            }
        };
        window.addEventListener('click', closeReduxPanelOnBlockCloseClick);
        return () => window.removeEventListener('click', closeReduxPanelOnBlockCloseClick);
    }, []);

    const getInitialSettings = () => {
        localStorageHyperDashboardTools.setInitialSettings();
        window.dispatchEvent(new Event('hyperDashboardSettingsReset'));
        areSettingsShown && toggleVisibility();
    };

    const renderSettings = () => {

        if (!settings) {
            return null;
        }

        const renderSettingEntry = (entry, inlineCluster = false) => {

            const [
                settingKey,
                {
                    text: settingNameForUi,
                    outputType,
                }
            ] = entry;

            if (!isSettingEntryVisible(entry, currentLocation)) {
                return null;
            }

            const handleInputChange = (e, isUpdateSkipped = undefined) => {

                const {
                    name,
                    value,
                    checked
                } = e.target;

                let valueForSave = '';

                switch (outputType) {
                    case 'number':
                        if (name === 'maxAchievedPage') {
                            const newState = { ...data };

                            dispatch(changeData({
                                ...newState,
                                maxAchievedPage: Number(value)
                            }));
                            if (!isUpdateSkipped) {
                                valueForSave = Number(value);
                                localStorageHyperDashboardTools.setValueByPropName(name, valueForSave);
                            }
                            return null;
                        }
                        valueForSave = Number(value);
                        break;
                    case 'string':
                    case 'object':
                        valueForSave = String(value); break;
                    case 'boolean':
                        valueForSave = Boolean(checked); break;
                }

                localStorageHyperDashboardTools.setValueByPropName(name, valueForSave);
            };

            const getInput = settingTuple => {

                const [
                    name,
                    {
                        value,
                        inputType,
                        options,
                        switchers,
                        modifier
                    }
                ] = settingTuple;

                switch (inputType) {
                    case 'regularInput':
                        return (
                            <RegularInputForSettings
                                { ...{
                                    value,
                                    settingName: name,
                                    switchers,
                                    handleInputChange
                                } }
                            />
                        );

                    case 'dropdown':
                        return (
                            <DropdownForSettings
                                { ...{
                                    value,
                                    settingName: name,
                                    handleInputChange,
                                    options,
                                    modifier,
                                    settings
                                } }
                            />
                        );
                    case 'checkbox':
                        return (
                            <CheckboxForSettings
                                { ...{
                                    value,
                                    settingName: name,
                                    handleInputChange
                                } }
                            />
                        );
                }
            };

            return (
                <span
                    key={ settingKey }
                    style={{
                        ...settingRowWrapper,
                        ...(inlineCluster ? settingRowWrapperInlineClusterStyle : {}),
                    }}
                >
                    <span>{ settingNameForUi }</span>
                    { getInput(entry) }
                </span>
            );
        };

        const entries = Object.entries(settings);

        const mainRows = entries
            .filter(([ k ]) => !consoleToolKeySet.has(k) && !TOP_LEVEL_SETTING_KEYS.has(k))
            .map(entry => renderSettingEntry(entry, false))
            .filter(Boolean);

        const consoleEntriesOrdered = CONSOLE_TOOL_KEYS
            .map(key => entries.find(([ k ]) => k === key))
            .filter(Boolean);

        const consoleRows = consoleEntriesOrdered
            .map(entry => renderSettingEntry(entry, true))
            .filter(Boolean);

        return (
            <>
                { mainRows }
                {
                    hasConsoleTools && consoleToolsOpen && (
                        <div style={ settingsAdvancedToolsGroupStyle }>
                            <div style={ settingsAdvancedToolsTogglesRowStyle }>
                                { consoleRows }
                            </div>
                        </div>
                    )
                }
            </>
        );
    };

    return (
        <Fragment>
            <div
                style={{
                    ...settingsShellBase,
                    ...(areSettingsShown ? settingsShellOpen : settingsShellCollapsed),
                }}
            >
                <div style={ settingsToolbarRowStyle }>
                    <button
                        type="button"
                        onClick={ () => toggleVisibility() }
                        aria-expanded={ areSettingsShown }
                        title={ areSettingsShown ? 'Hide settings' : 'Show settings' }
                        aria-label={ areSettingsShown ? 'Hide settings' : 'Show settings' }
                        style={{
                            ...darculaIconButtonBase,
                            ...(areSettingsShown ? settingsTogglePressed : settingsToggleReleased),
                        }}
                    >
                        <FontAwesomeIcon icon={ faCog } aria-hidden />
                    </button>
                    {
                        areSettingsShown && hasConsoleTools && (
                            <button
                                type="button"
                                onClick={ () => setConsoleToolsOpen(o => !o) }
                                aria-expanded={ consoleToolsOpen }
                                title={ consoleToolsOpen ? 'Hide console tools' : 'Show console tools' }
                                aria-label={ consoleToolsOpen ? 'Hide console tools' : 'Show console tools' }
                                style={{
                                    ...darculaIconButtonBase,
                                    ...(consoleToolsOpen ? settingsTogglePressed : settingsToggleReleased),
                                }}
                            >
                                <FontAwesomeIcon icon={ faWrench } aria-hidden />
                            </button>
                        )
                    }
                    <button
                        type="button"
                        onClick={ () => setIsReduxStoreOpen(o => !o) }
                        aria-expanded={ isReduxStoreOpen }
                        title={ isReduxStoreOpen ? 'Hide Redux store' : 'Show Redux store' }
                        aria-label={ isReduxStoreOpen ? 'Hide Redux store' : 'Show Redux store' }
                        style={{
                            ...darculaIconButtonBase,
                            ...(isReduxStoreOpen ? settingsTogglePressed : settingsToggleReleased),
                        }}
                    >
                        <FontAwesomeIcon icon={ faDatabase } aria-hidden />
                    </button>
                    <button
                        type="button"
                        onClick={ () => getInitialSettings() }
                        title="Initial Settings"
                        aria-label="Initial Settings"
                        style={{
                            ...darculaIconButtonBase,
                            ...settingsResetButtonStyle,
                        }}
                    >
                        <FontAwesomeIcon icon={ faUndo } aria-hidden />
                    </button>
                </div>

                <div>
                    {
                        areSettingsShown && renderSettings()
                    }
                </div>
            </div>
            {
                isReduxStoreOpen &&
                <ReduxStoreInfo
                    id="ReduxStore"
                    storeData={ store.getState() }
                />
            }
        </Fragment>
    );
};

export default Settings;
